import { type FormEvent, type JSX, type ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { CampaignChannelChooser } from '@/components/CampaignChannelChooser';
import { Icon } from '@/components/icons';
import {
  Button,
  Card,
  ConfirmModal,
  IconAction,
  Input,
  Progress,
  Skeleton,
  Spinner,
  StatusPill,
} from '@/components/ui';
import {
  useCampaigns,
  useCancelCampaign,
  useDeleteCampaign,
  useLaunchCampaign,
  useScheduleCampaign,
  useUnscheduleCampaign,
  type CampaignItem,
} from '@/hooks/useCampaigns';
import { useFeature } from '@/hooks/useFeatures';
import { ApiError } from '@/lib/api';
import { formatRelativeTime } from '@/lib/format';
import { cn } from '@/lib/utils';


// CampaignWizard removed - replaced by the dedicated /campaigns/new and
// /campaigns/:id/edit routes (see pages/campaign-wizard.tsx).

// ---------------------------------------------------------------------------
// Modal shell (same as contacts)
// ---------------------------------------------------------------------------
function ModalShell({
  title,
  onClose,
  children,
  wide,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}): JSX.Element {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink/50 px-4">
      <div
        className={cn(
          'bg-surface border border-line rounded-lg shadow-lg w-full max-h-[90vh] overflow-auto',
          wide ? 'max-w-[920px]' : 'max-w-[560px]',
        )}
      >
        <div className="px-5 py-4 border-b border-line flex items-center justify-between">
          <h2 className="display text-[16px] font-semibold text-text">{title}</h2>
          <button
            onClick={onClose}
            className="w-7 h-7 grid place-items-center rounded-md text-text-3 hover:bg-surface-3"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

// CampaignDetailModal removed - campaign detail now lives at the dedicated
// /campaigns/:id route (see pages/campaign-detail.tsx). The page-level`r
// "View" button navigates there instead of opening a modal. Helpers used`r
// only by the old modal (DetailKpi, messageStatusVariant) went with it.

// ---------------------------------------------------------------------------
// Schedule modal — datetime picker + timezone select
// ---------------------------------------------------------------------------
interface ScheduleModalProps {
  campaign: CampaignItem | null;
  onClose: () => void;
}

// Common timezones the operator might pick. Kept short on purpose — full
// IANA list is overwhelming and 99% of users sit in one of these.
const TIMEZONE_OPTIONS = [
  'America/Bogota',
  'America/New_York',
  'America/Mexico_City',
  'America/Chicago',
  'America/Los_Angeles',
  'America/Argentina/Buenos_Aires',
  'America/Sao_Paulo',
  'Europe/Madrid',
  'UTC',
];

// guessTimezone returns the browser's IANA timezone if it appears in the
// short list, otherwise the first option. Better than always defaulting
// to NY for our LATAM-leaning audience.
function guessTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (TIMEZONE_OPTIONS.includes(tz)) return tz;
  } catch {
    // Browser doesn't support Intl options — fall through.
  }
  return TIMEZONE_OPTIONS[0]!;
}

// nextHourLocal returns a `<input type="datetime-local">`-compatible
// string for "now + 1h" rounded to the next quarter hour, in the user's
// browser local time. This gives the picker a sensible non-past default
// that the operator just confirms or tweaks.
function nextHourLocal(): string {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  d.setMinutes(Math.ceil(d.getMinutes() / 15) * 15, 0, 0);
  // Format as YYYY-MM-DDTHH:mm in local time.
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function ScheduleModal({ campaign, onClose }: ScheduleModalProps): JSX.Element | null {
  const { t } = useTranslation('default');
  const [when, setWhen] = useState(nextHourLocal());
  const [timezone, setTimezone] = useState(guessTimezone());
  const [error, setError] = useState<string | null>(null);
  const schedule = useScheduleCampaign();

  useEffect(() => {
    if (!campaign) {
      setWhen(nextHourLocal());
      setTimezone(guessTimezone());
      setError(null);
    }
  }, [campaign]);

  if (!campaign) return null;

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (!when) {
      setError(t('campaigns_schedule_err_when_required'));
      return;
    }
    // The datetime-local input gives us "YYYY-MM-DDTHH:mm" in the user's
    // local time, no offset. Convert to a real Date so the browser
    // applies its own offset, then send as RFC3339 (toISOString gives
    // UTC, which the backend accepts and re-stamps in UTC anyway).
    const dt = new Date(when);
    if (Number.isNaN(dt.getTime())) {
      setError(t('campaigns_schedule_err_invalid'));
      return;
    }
    if (dt.getTime() <= Date.now()) {
      setError(t('campaigns_schedule_err_past'));
      return;
    }
    try {
      await schedule.mutateAsync({
        id: campaign.id,
        scheduled_at: dt.toISOString(),
        timezone,
      });
      onClose();
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError(err instanceof Error ? err.message : t('campaigns_err_generic'));
    }
  };

  return (
    <ModalShell title={t('campaigns_schedule_title', { name: campaign.name })} onClose={onClose}>
      {error && (
        <div className="px-3 py-2 mb-3 bg-danger-bg border border-danger-bd rounded-md text-[13px] text-danger">
          {error}
        </div>
      )}
      <form onSubmit={submit}>
        <label className="block text-[12px] font-medium text-text-2 mb-1.5" htmlFor="sch-when">
          {t('campaigns_schedule_when')}
        </label>
        <Input
          id="sch-when"
          type="datetime-local"
          value={when}
          onChange={(e) => setWhen(e.target.value)}
          disabled={schedule.isPending}
        />

        <label className="block text-[12px] font-medium text-text-2 mb-1.5 mt-3" htmlFor="sch-tz">
          {t('campaigns_schedule_tz')}
        </label>
        <select
          id="sch-tz"
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          disabled={schedule.isPending}
          className="w-full h-10 px-3 text-sm bg-surface text-text border border-line-strong rounded-md outline-none focus:border-volt-500 focus:ring-[3px] focus:ring-volt-glow disabled:opacity-50"
        >
          {TIMEZONE_OPTIONS.map((tz) => (
            <option key={tz} value={tz}>
              {tz}
            </option>
          ))}
        </select>
        <p className="text-[12px] text-text-4 mt-2">{t('campaigns_schedule_help')}</p>

        <div className="flex justify-end gap-2 mt-5">
          <Button type="button" variant="secondary" onClick={onClose} disabled={schedule.isPending}>
            {t('campaigns_cancel')}
          </Button>
          <Button type="submit" disabled={schedule.isPending}>
            {schedule.isPending ? (
              <>
                <Spinner />
                <span>{t('campaigns_scheduling')}</span>
              </>
            ) : (
              t('campaigns_schedule_confirm')
            )}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function CampaignsPage(): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);

  const visible = useTabVisibility();
  const navigate = useNavigate();
  const voiceOn = useFeature('voice');
  // Voice-enabled tenants pick SMS vs Voice first; SMS-only tenants go
  // straight to the SMS wizard (chooser never mounts) — unchanged behavior.
  const [channelChooserOpen, setChannelChooserOpen] = useState(false);
  const onNewCampaign = () =>
    voiceOn ? setChannelChooserOpen(true) : navigate('/campaigns/new');
  const list = useCampaigns({ enabled: visible });
  const launch = useLaunchCampaign();
  const del = useDeleteCampaign();
  const cancel = useCancelCampaign();
  const unschedule = useUnscheduleCampaign();
  const [scheduleTarget, setScheduleTarget] = useState<CampaignItem | null>(null);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const [launchOk, setLaunchOk] = useState<string | null>(null);

  // Pending confirm dialog: store the campaign + the action so the modal
  // can render the right copy and dispatch the matching mutation.
  const [pendingAction, setPendingAction] = useState<{
    kind: 'launch' | 'delete' | 'cancel' | 'unschedule';
    campaign: CampaignItem;
  } | null>(null);

  const items = list.data?.items ?? [];

  const handleEdit = (c: CampaignItem) => navigate(`/campaigns/${c.id}/edit`);

  const askDelete = (c: CampaignItem) => setPendingAction({ kind: 'delete', campaign: c });
  const askLaunch = (c: CampaignItem) => setPendingAction({ kind: 'launch', campaign: c });
  const askCancel = (c: CampaignItem) => setPendingAction({ kind: 'cancel', campaign: c });
  const askUnschedule = (c: CampaignItem) => setPendingAction({ kind: 'unschedule', campaign: c });
  const askSchedule = (c: CampaignItem) => setScheduleTarget(c);

  const closeConfirm = () => setPendingAction(null);

  const runConfirm = async () => {
    if (!pendingAction) return;
    setLaunchError(null);
    setLaunchOk(null);
    const { kind, campaign } = pendingAction;
    try {
      if (kind === 'launch') {
        const r = await launch.mutateAsync(campaign.id);
        setLaunchOk(
          t('campaigns_launch_ok', {
            enqueued: r.enqueued.toLocaleString(lang),
            total: r.total_recipients.toLocaleString(lang),
            failed: r.failed.toLocaleString(lang),
          }),
        );
      } else if (kind === 'delete') {
        await del.mutateAsync(campaign.id);
      } else if (kind === 'cancel') {
        await cancel.mutateAsync(campaign.id);
      } else if (kind === 'unschedule') {
        await unschedule.mutateAsync(campaign.id);
      }
      setPendingAction(null);
    } catch (err) {
      setLaunchError(err instanceof Error ? err.message : t('campaigns_err_generic'));
      setPendingAction(null);
    }
  };

  const confirmCopy = (() => {
    if (!pendingAction) return null;
    const { kind, campaign } = pendingAction;
    switch (kind) {
      case 'launch':
        return {
          title: t('campaigns_launch'),
          description: t('campaigns_confirm_launch', { name: campaign.name }),
          confirmLabel: t('campaigns_launch'),
          tone: 'primary' as const,
        };
      case 'delete':
        return {
          title: t('campaigns_delete'),
          description: t('campaigns_confirm_delete', { name: campaign.name }),
          confirmLabel: t('campaigns_delete'),
          tone: 'danger' as const,
        };
      case 'cancel':
        return {
          title: t('campaigns_cancel_title'),
          description: t('campaigns_confirm_cancel', { name: campaign.name }),
          confirmLabel: t('campaigns_cancel_action'),
          tone: 'danger' as const,
        };
      case 'unschedule':
        return {
          title: t('campaigns_unschedule_title'),
          description: t('campaigns_confirm_unschedule', { name: campaign.name }),
          confirmLabel: t('campaigns_unschedule_action'),
          tone: 'danger' as const,
        };
    }
  })();
  const confirmBusy =
    pendingAction?.kind === 'launch'
      ? launch.isPending
      : pendingAction?.kind === 'delete'
        ? del.isPending
        : pendingAction?.kind === 'cancel'
          ? cancel.isPending
          : pendingAction?.kind === 'unschedule'
            ? unschedule.isPending
            : false;

  return (
    <div className="px-8 py-8 max-w-[1200px] mx-auto">
      <header className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h1 className="display text-[28px] font-semibold tracking-tight text-text leading-tight m-0">
            {t('nav_campaigns')}
          </h1>
          <p className="text-[13px] text-text-3 mt-1">
            {items.length === 0
              ? t('campaigns_sub')
              : (() => {
                  const running = items.filter((c) => c.status === 'running').length;
                  return running > 0
                    ? t('campaigns_count_with_running', {
                        total: items.length.toLocaleString(lang),
                        running: running.toLocaleString(lang),
                      })
                    : t('campaigns_count_only', {
                        total: items.length.toLocaleString(lang),
                      });
                })()}
          </p>
        </div>
        <Button onClick={onNewCampaign}>
          <Icon.plus width="14" height="14" />
          {t('campaigns_new')}
        </Button>
      </header>

      {launchOk && (
        <Card padding="md" className="mb-4 border-success-bd bg-success-bg">
          <p className="text-[13px] text-success">{launchOk}</p>
        </Card>
      )}
      {launchError && (
        <Card padding="md" className="mb-4 border-danger-bd bg-danger-bg">
          <p className="text-[13px] text-danger">{launchError}</p>
        </Card>
      )}

      <Card padding="none">
        {list.isLoading ? (
          <div className="p-5 space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <CampaignsEmpty onCreate={onNewCampaign} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-text-4 bg-surface-2 border-b border-line">
                  <th className="px-5 py-2.5 font-medium">{t('campaigns_col_name')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('campaigns_col_status')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('campaigns_col_sent')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('campaigns_col_delivered')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('campaigns_col_failed')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('campaigns_col_when')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('campaigns_col_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <CampaignRow
                    key={c.id}
                    c={c}
                    lang={lang}
                    t={t as (k: string, opts?: Record<string, unknown>) => string}
                    onLaunch={() => askLaunch(c)}
                    onSchedule={() => askSchedule(c)}
                    onUnschedule={() => askUnschedule(c)}
                    onView={() => navigate(`/campaigns/${c.id}`)}
                    onEdit={() => handleEdit(c)}
                    onDelete={() => askDelete(c)}
                    onCancel={() => askCancel(c)}
                    busy={launch.isPending || del.isPending || cancel.isPending || unschedule.isPending}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ScheduleModal campaign={scheduleTarget} onClose={() => setScheduleTarget(null)} />

      {voiceOn && (
        <CampaignChannelChooser
          open={channelChooserOpen}
          onClose={() => setChannelChooserOpen(false)}
          smsTo="/campaigns/new"
        />
      )}

      {confirmCopy && (
        <ConfirmModal
          open={pendingAction !== null}
          title={confirmCopy.title}
          description={confirmCopy.description}
          confirmLabel={confirmCopy.confirmLabel}
          cancelLabel={t('campaigns_cancel')}
          tone={confirmCopy.tone}
          busy={confirmBusy}
          onConfirm={() => void runConfirm()}
          onClose={closeConfirm}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state — first-run pattern from the design handoff: dashed-border
// brand glyph + H2 + paragraph + dual CTA. Lives inline rather than in a
// shared primitive because Senders/Contacts may need their own glyph and
// copy when their empty states get the same treatment.
// ---------------------------------------------------------------------------

interface CampaignsEmptyProps {
  onCreate: () => void;
}

function CampaignsEmpty({ onCreate }: CampaignsEmptyProps): JSX.Element {
  const { t } = useTranslation('default');
  const navigate = useNavigate();
  return (
    <div className="px-8 py-16 text-center">
      <div
        aria-hidden="true"
        className="mx-auto w-24 h-24 grid place-items-center mb-6 rounded-md border border-dashed border-line-strong bg-surface-2"
      >
        <svg width="40" height="40" viewBox="0 0 64 64" fill="none">
          <rect x="14" y="14" width="36" height="36" rx="8" fill="var(--volt)" />
          <path
            d="M22 38 L42 26 M42 26 L36 26 M42 26 L42 32"
            stroke="var(--volt-ink)"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <h2 className="display text-[20px] font-semibold text-text mb-2">
        {t('campaigns_empty_title')}
      </h2>
      <p className="text-[13px] text-text-3 max-w-[420px] mx-auto mb-6">
        {t('campaigns_empty_sub')}
      </p>
      <div className="flex justify-center gap-2.5">
        <Button variant="secondary" onClick={() => navigate('/contacts')}>
          {t('campaigns_empty_import')}
        </Button>
        <Button onClick={onCreate}>
          <Icon.plus width="14" height="14" />
          {t('campaigns_empty_create')}
        </Button>
      </div>
    </div>
  );
}

interface RowProps {
  c: CampaignItem;
  lang: string;
  t: (k: string, opts?: Record<string, unknown>) => string;
  onLaunch: () => void;
  onSchedule: () => void;
  onUnschedule: () => void;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onCancel: () => void;
  busy: boolean;
}

function CampaignRow({
  c,
  lang,
  t,
  onLaunch,
  onSchedule,
  onUnschedule,
  onView,
  onEdit,
  onDelete,
  onCancel,
  busy,
}: RowProps): JSX.Element {
  const isDraft = c.status === 'draft';
  const isRunning = c.status === 'running';
  const isScheduled = c.status === 'scheduled';
  // Cancel is offered for any in-flight state — running, queued or scheduled
  // — so the operator can stop a stuck or unwanted send without dropping
  // into SQL. Backend rejects it for terminal states. For scheduled
  // specifically we use the Unschedule button instead (cleaner UX —
  // returns to draft so the operator can re-schedule).
  const cancellable = c.status === 'running' || c.status === 'queued';
  // Mini progress bar in the Sent column. Volt while live, success on
  // completed, neutral grey on draft/scheduled/cancelled. Only renders when
  // there's a denominator (total_recipients > 0).
  const progressColor =
    c.status === 'running' || c.status === 'queued'
      ? 'var(--volt-500)'
      : c.status === 'completed'
        ? 'var(--success)'
        : c.status === 'failed' || c.status === 'cancelled'
          ? 'var(--danger)'
          : 'var(--text-4)';
  return (
    <tr
      className="border-b border-line last:border-0 hover:bg-surface-2 cursor-pointer"
      onClick={onView}
    >
      <td className="px-5 py-3 text-text font-medium">{c.name}</td>
      <td className="px-5 py-3">
        <StatusPill status={c.status} />
        {isScheduled && c.scheduled_at && (
          <div
            className="text-[11px] text-text-3 mt-1"
            title={new Date(c.scheduled_at).toLocaleString(lang)}
          >
            {t('campaigns_scheduled_for', {
              when: new Date(c.scheduled_at).toLocaleString(lang, {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              }),
            })}
          </div>
        )}
      </td>
      <td className="px-5 py-3 min-w-[160px]">
        <div className="num text-text-2 whitespace-nowrap">
          {c.sent_count.toLocaleString(lang)}
          <span className="text-text-4"> / {c.total_recipients.toLocaleString(lang)}</span>
        </div>
        {c.total_recipients > 0 && (
          <Progress
            value={c.sent_count}
            total={c.total_recipients}
            color={progressColor}
            className="mt-1.5"
          />
        )}
      </td>
      <td className="px-5 py-3 num text-right text-success">
        {c.delivered_count.toLocaleString(lang)}
      </td>
      <td className="px-5 py-3 num text-right text-danger">
        {c.failed_count.toLocaleString(lang)}
      </td>
      <td className="px-5 py-3 text-text-3 whitespace-nowrap">
        <span title={new Date(c.created_at).toLocaleString(lang)}>
          {formatRelativeTime(c.created_at, lang)}
        </span>
      </td>
      <td
        className="px-5 py-3 text-right"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-end gap-0.5">
          {isDraft && (
            <>
              <IconAction icon={Icon.play} label={t('campaigns_launch')} onClick={onLaunch} disabled={busy} tone="primary" />
              <IconAction icon={Icon.calendar} label={t('campaigns_schedule')} onClick={onSchedule} disabled={busy} />
            </>
          )}
          <IconAction icon={Icon.eye} label={t('campaigns_view')} onClick={onView} />
          {isDraft && (
            <IconAction icon={Icon.pencil} label={t('campaigns_edit')} onClick={onEdit} disabled={busy} />
          )}
          {isScheduled && (
            <IconAction icon={Icon.xMark} label={t('campaigns_unschedule_action')} onClick={onUnschedule} disabled={busy} tone="warn" />
          )}
          {cancellable && (
            <IconAction icon={Icon.stop} label={t('campaigns_cancel_action')} onClick={onCancel} disabled={busy} tone="warn" />
          )}
          {!isRunning && (
            <IconAction icon={Icon.trash} label={t('campaigns_delete')} onClick={onDelete} disabled={busy} tone="danger" />
          )}
        </div>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Tab visibility hook (reused pattern from messages/dashboard)
// ---------------------------------------------------------------------------
function useTabVisibility(): boolean {
  const [visible, setVisible] = useState<boolean>(() =>
    typeof document === 'undefined' ? true : document.visibilityState === 'visible',
  );
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onChange = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);
  return visible;
}

